import React from 'react';
import { Phone, MessageCircle, Heart, Award, Clock, Users, CheckCircle } from 'lucide-react';
import { ownerInfo, features } from '../data/mock';
import { ScrollReveal } from './ui/scroll-reveal';

const AboutSection = () => {
  const featureIcons = [Heart, Award, Clock, Users];

  const stats = [
    { value: '+1000', label: 'Services Completed' },
    { value: '8+', label: 'Years Experience' },
    { value: '100%', label: 'Satisfaction' }
  ];

  return (
    <section id="about" className="section">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left - About Content */}
          <div>
            <ScrollReveal animation="fadeUp">
              <span 
                className="inline-block text-sm font-semibold uppercase tracking-wider mb-4 px-4 py-1.5 rounded-full"
                style={{ 
                  color: 'var(--accent-green)',
                  background: 'var(--accent-green-light)'
                }}
              >
                About Us
              </span>
            </ScrollReveal>

            <ScrollReveal animation="fadeUp" delay={100}>
              <h2 className="heading-1 mb-6">
                Meet {ownerInfo.name.split(' ')[0]}
              </h2>
            </ScrollReveal>

            <ScrollReveal animation="fadeUp" delay={200}>
              <p 
                className="text-lg leading-relaxed mb-8"
                style={{ color: 'var(--text-secondary)' }}
              >
                {ownerInfo.bio}
              </p>
            </ScrollReveal>

            {/* Stats */}
            <ScrollReveal animation="fadeUp" delay={300}>
              <div className="flex flex-wrap gap-8 mb-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <p 
                      className="text-3xl font-bold mb-1"
                      style={{ color: 'var(--navy-primary)' }}
                    >
                      {stat.value}
                    </p>
                    <p 
                      className="text-sm font-medium"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </ScrollReveal>
            
            {/* Contact Buttons */}
            <ScrollReveal animation="fadeUp" delay={400}>
              <div className="flex flex-wrap gap-4">
                <a 
                  href={`tel:${ownerInfo.phone.replace(/[^0-9]/g, '')}`}
                  className="btn-primary"
                  style={{ boxShadow: '0 8px 20px rgba(26, 62, 92, 0.2)' }}
                >
                  <Phone size={18} />
                  Call {ownerInfo.phone}
                </a>
                <a 
                  href={`https://wa.me/1${ownerInfo.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent("Hello! I'd like to request a free quote for cleaning services.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary"
                >
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </a>
              </div>
            </ScrollReveal>
          </div>

          {/* Right - Features Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {features.map((feature, index) => {
              const Icon = featureIcons[index];
              const colors = [
                { bg: 'var(--accent-blue-light)', icon: 'var(--accent-blue)' },
                { bg: 'var(--accent-gold-light)', icon: 'var(--accent-gold)' },
                { bg: 'var(--accent-green-light)', icon: 'var(--accent-green)' },
                { bg: 'var(--accent-blue-light)', icon: 'var(--accent-blue)' }
              ];

              return (
                <ScrollReveal 
                  key={feature.id} 
                  animation="fadeUp"
                  delay={index * 100}
                >
                  <div 
                    className="p-6 rounded-2xl transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                    style={{ 
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border-light)'
                    }}
                  >
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                      style={{ background: colors[index].bg }}
                    >
                      <Icon size={22} style={{ color: colors[index].icon }} strokeWidth={1.5} />
                    </div>
                    <h4 
                      className="font-semibold text-lg mb-2"
                      style={{ color: 'var(--text-primary)' }}
                    >
                      {feature.title}
                    </h4>
                    <p 
                      className="text-sm leading-relaxed"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {feature.description}
                    </p>
                  </div>
                </ScrollReveal>
              );
            })}
          </div>
        </div>

        {/* Trust Indicators */}
        <ScrollReveal animation="fadeUp" delay={500}>
          <div 
            className="mt-16 p-8 rounded-2xl text-center"
            style={{ 
              background: 'linear-gradient(135deg, var(--accent-blue-light) 0%, var(--accent-green-light) 100%)',
              border: '1px solid var(--border-light)'
            }}
          >
            <div className="flex flex-wrap justify-center gap-8 md:gap-16">
              {[
                'Licensed & Insured',
                'Background Checked',
                'Eco-Friendly Products',
                'Satisfaction Guaranteed'
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckCircle size={18} style={{ color: 'var(--accent-green)' }} />
                  <span 
                    className="text-sm font-medium"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
};

export default AboutSection;
